/**
 * @file Disco-daily front-end cache.
 * @copyright Copyright (c) 2024-2026 Woolly Bear Software,
 * all rights reserved.
 */
export const CONFIG_CACHE = "user-gen-config";
export const HOLIDAY_CONFIG_CACHE = "user-holiday-config";
export const LAST_MIX = "user-last-disco-mix";
export const LAST_HOLIDAY_MIX = "user-last-holiday-mix";
// Local-first caches for the full models/mixes lists (the source of truth
// for those lists is local storage - the backend is a sync target, not the
// primary read path). A returning user whose local storage only has the
// old single-mix/single-model cache keys above gets migrated into these on
// their first load after this feature shipped; see App.initApp().
export const MODELS_CACHE = "user-models";
export const MIXES_CACHE = "user-mixes";
// Which mix's tracks currently back the single shared "Unheard.FM |
// Discovery" Spotify playlist, and that playlist's id - local-first for the
// same reason as MODELS_CACHE/MIXES_CACHE: without this cached, a failed or
// slow account fetch on load makes the app think no Discovery playlist
// exists yet and it creates a duplicate.
export const ACTIVE_DISCOVERY_PLAYLIST_CACHE = "user-active-discovery-playlist";
// Same local-first rationale as ACTIVE_DISCOVERY_PLAYLIST_CACHE, but for the
// single shared "Unheard.FM | Liked Tracks" playlist that individual track
// likes get appended to.
export const LIKED_TRACKS_PLAYLIST_CACHE = "user-liked-tracks-playlist";
// Cache to presit liked track sync to playlist jobs.
export const LIKED_TRACKS_SYNC_QUEUE_CACHE = "user-liked-tracks-sync-queue";
// Id of whichever mix (in MIXES_CACHE) was last viewed/edited - the
// Discovery nav tab lands on this mix on reload rather than always the
// most recently generated one. Local-only by design (see App.resolveActiveMix).
export const ACTIVE_MIX_ID_CACHE = "user-active-mix-id";
// Whether Spotify links (track/artist/album/playlist/profile) should open
// the desktop app (via its spotify: URI scheme) instead of the web
// player. Desktop-only concern - mobile already gets this via OS
// universal links - and per-device by design (see utils.getSpotifyLinkHref),
// not synced to the backend account like sync_discovery_config is.
export const PREFER_SPOTIFY_APP_CACHE = "prefer-spotify-app";
export const USER_TOP_TRACKS_CACHE = "user-top-tracks";
export const PLAYLIST_CACHE = "user-playlist-tracks";
export const USER_ACK_EMAIL = "user-ack-email";
export const USER_STATS = "user-stats";
export const PWA_PROMPT_SHOWN = "pwa-prompt-shown";
export const USER_ACK_WELCOME = "user-ack-welcome";
export const USER_ACK_SYNC_BANNER = "user-ack-sync-banner";
// Whether the user has already been shown (and answered or dismissed) the
// one-time "Open Spotify Links in the App?" dialog - see
// spotify-link-prompt.tsx. Set on any resolution, not just an explicit
// choice, so it only ever asks once.
export const USER_ACK_SPOTIFY_APP_PROMPT = "user-ack-spotify-app-prompt";
// Whether the user has already seen the one-time "your liked tracks go to
// this playlist" explainer, shown the first time they like a track. Set on
// close, so it only ever shows once.
export const USER_ACK_FIRST_LIKE = "user-ack-first-like";

export const CacheType = Object.freeze({
  LOCAL: "local",
  SESSION: "session",
  IN_MEM: "in-mem",
});

export type CacheTypeValue = (typeof CacheType)[keyof typeof CacheType];

const CachePolicy: Record<string, CacheTypeValue> = {
  [CONFIG_CACHE]: CacheType.LOCAL,
  [LAST_MIX]: CacheType.LOCAL,
  [USER_ACK_EMAIL]: CacheType.LOCAL,
  [USER_STATS]: CacheType.LOCAL,
  [HOLIDAY_CONFIG_CACHE]: CacheType.LOCAL,
  [LAST_HOLIDAY_MIX]: CacheType.LOCAL,
  [PWA_PROMPT_SHOWN]: CacheType.SESSION,
  [USER_ACK_WELCOME]: CacheType.LOCAL,
  [USER_ACK_SYNC_BANNER]: CacheType.LOCAL,
  [MODELS_CACHE]: CacheType.LOCAL,
  [MIXES_CACHE]: CacheType.LOCAL,
  [ACTIVE_DISCOVERY_PLAYLIST_CACHE]: CacheType.LOCAL,
  [LIKED_TRACKS_PLAYLIST_CACHE]: CacheType.LOCAL,
  [LIKED_TRACKS_SYNC_QUEUE_CACHE]: CacheType.LOCAL,
  [ACTIVE_MIX_ID_CACHE]: CacheType.LOCAL,
  [PREFER_SPOTIFY_APP_CACHE]: CacheType.LOCAL,
  [USER_ACK_SPOTIFY_APP_PROMPT]: CacheType.LOCAL,
  [USER_ACK_FIRST_LIKE]: CacheType.LOCAL,
};

const MEM_CACHE: Record<string, string> = {};

/**
 * Caching object.
 */
class CacheClass {
  write(key: string, data: unknown, type: CacheTypeValue | null = null): void {
    const resolvedType = type || CachePolicy[key] || CacheType.LOCAL;
    const jsonData = JSON.stringify(data);

    try {
      switch (resolvedType) {
        case CacheType.LOCAL:
          localStorage.setItem(key, jsonData);
          break;
        case CacheType.SESSION:
          sessionStorage.setItem(key, jsonData);
          break;
        case CacheType.IN_MEM:
          MEM_CACHE[key] = jsonData;
          break;
      }
    } catch (e) {
      console.error("failed to write to cache:", e);
      throw e;
    }
  }

  read<T = unknown>(key: string, type: CacheTypeValue | null = null): T | null {
    const resolvedType = type || CachePolicy[key] || CacheType.LOCAL;
    let data: string | null = null;

    switch (resolvedType) {
      case CacheType.LOCAL:
        data = localStorage.getItem(key) || null;
        break;
      case CacheType.SESSION:
        data = sessionStorage.getItem(key) || null;
        break;
      case CacheType.IN_MEM:
        data = MEM_CACHE[key] || null;
        break;
    }

    return data ? (JSON.parse(data) as T) : null;
  }

  remove(key: string, type: CacheTypeValue | null = null): void {
    const resolvedType = type || CachePolicy[key] || CacheType.LOCAL;

    switch (resolvedType) {
      case CacheType.LOCAL:
        localStorage.removeItem(key);
        break;
      case CacheType.SESSION:
        sessionStorage.removeItem(key);
        break;
      case CacheType.IN_MEM:
        delete MEM_CACHE[key];
        break;
    }
  }
}

export const Cache = new CacheClass();
